<template>
  <div class="development-competency-detail-page">
    <nav id="breadcrumbs" class="mb-3" :aria-label="$t('general.page_path')">
      <ul>
        <li>
          <router-link :to="{ name: 'company.dashboard' }">
            <i class="uil-home-alt"></i>
          </router-link>
        </li>
        <li>
          <router-link :to="{ name: 'company.development-evaluation.index' }">
            {{ $t('general.development_evaluation') }}
          </router-link>
        </li>
        <li v-if="user">
          <router-link
            :to="{
              name: 'company.development-evaluation.user',
              params: { userId: currentUserId },
            }"
          >
            {{ user.full_name || $t('general.user') }}
          </router-link>
        </li>
        <li>{{ (assignment && assignment.name) || $t('general.competency_detail') }}</li>
      </ul>
    </nav>

    <default-loading v-if="loading"></default-loading>

    <div v-else-if="notFound" class="card empty-state">
      <i class="uil-exclamation-circle"></i>
      <h3>{{ $t('general.competency_not_found') }}</h3>
      <p>{{ $t('general.admin_competency_not_found_hint') }}</p>
      <router-link
        :to="{
          name: 'company.development-evaluation.user',
          params: { userId: currentUserId },
        }"
        class="btn btn-default"
      >
        {{ $t('general.back_to_user_competencies') }}
      </router-link>
    </div>

    <template v-else-if="assignment">
      <section v-if="user" class="card user-context-card">
        <AvatarOrIcon :alt="user.full_name || $t('general.user')"
                      :size-px="50"
                      :src="user.avatar || ''"
        />
        <div class="context-user-info">
          <span>{{ $t('general.evaluated_user') }}</span>
          <strong>{{ user.full_name || '-' }}</strong>
          <small>{{ userOrganization }}</small>
        </div>
        <router-link
          :to="{
            name: 'company.development-evaluation.user',
            params: { userId: currentUserId },
          }"
          class="btn btn-light btn-sm"
        >
          {{ $t('general.all_competencies') }}
        </router-link>
      </section>

      <AlertMessage v-if="errorMessage" :message="{ type: 'error', text: errorMessage }" @remove="errorMessage = ''" />
      <AlertMessage v-if="successMessage" :message="{ type: 'success', text: successMessage }" @remove="successMessage = ''" />

      <div class="detail-view-wrapper">
        <AssignedCompetencyDetail
          viewer="admin"
          :assignment="assignment"
          :comments="comments"
          :can-comment="canComment"
          :allow-comment-visibility="true"
          :submitting="submittingComment"
          @submit-comment="submitAdminComment"
        />
      </div>
    </template>
  </div>
</template>

<script>
import DefaultLoading from "@/view/components/loading/DefaultLoading.vue";
import AlertMessage from "@/view/components/notification/AlertMessage.vue";
import AvatarOrIcon from "@/view/components/icons/AvatarOrIcon.vue";
import AssignedCompetencyDetail from "@/view/components/developmentCompetencies/AssignedCompetencyDetail.vue";
import ApiService from "@/core/services/api.service";
import querystring from "querystring";

export default {
  name: "DevelopmentEvaluationCompetencyDetail",
  components: {
    AlertMessage,
    AssignedCompetencyDetail,
    AvatarOrIcon,
    DefaultLoading,
  },
  props: {
    userId: {
      type: [Number, String],
      default: null,
    },
    userCompetencyId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      user: null,
      assignment: null,
      comments: [],
      loading: true,
      submittingComment: false,
      notFound: false,
      errorMessage: "",
      successMessage: "",
      requestSequence: 0,
    };
  },
  computed: {
    currentUserId() {
      return this.userId || this.$route.params.userId;
    },
    currentCompetencyId() {
      return this.userCompetencyId || this.$route.params.userCompetencyId;
    },
    canComment() {
      return this.$hasPermission("read_competency");
    },
    userOrganization() {
      if (!this.user) return "-";
      return (
        [this.user.department_name, this.user.title_name]
          .filter(Boolean)
          .join(" / ") ||
        this.user.email ||
        "-"
      );
    },
  },
  watch: {
    currentUserId(newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.loadDetail();
      }
    },
    currentCompetencyId(newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.loadDetail();
      }
    },
  },
  mounted() {
    this.loadDetail();
  },
  beforeDestroy() {
    this.requestSequence += 1;
  },
  methods: {
    async loadDetail(showLoader = true) {
      if (!this.currentUserId || !this.currentCompetencyId) {
        this.notFound = true;
        this.loading = false;
        return;
      }

      const requestId = ++this.requestSequence;
      if (showLoader) this.loading = true;
      this.notFound = false;
      this.errorMessage = "";

      try {
        // Detay ve kullanıcı özeti; kullanıcı bilgisi liste endpoint'inden alınıyor
        const [detailResponse, userResponse] = await Promise.all([
          ApiService.get(
            `api/competencies-evaluation/${this.currentUserId}/${this.currentCompetencyId}`
          ),
          ApiService.get(
            `api/competencies-evaluation/${this.currentUserId}?${querystring.stringify(
              { page: 1, perPage: 1 }
            )}`
          ),
        ]);

        if (requestId !== this.requestSequence) return;

        const payload = detailResponse?.data || {};
        const item = payload.data || payload;
        if (!item || item.id == null) {
          this.notFound = true;
          return;
        }

        this.assignment = {
          id: item.id,
          name: item.name,
          description: item.description,
          assigned_at: item.assigned_at,
          updated_at: item.updated_at,
          comments_count: item.comments_count,
          latest_target: item.latest_target ?? null,
        };
        this.comments = Array.isArray(item.comments) ? item.comments : [];
        this.user =
          userResponse?.data?.user || this.user || { id: this.currentUserId };
      } catch (error) {
        if (requestId !== this.requestSequence) return;
        if (error?.response?.status === 404) {
          this.notFound = true;
        } else {
          this.errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            this.$t("general.competency_detail_load_error");
        }
      } finally {
        if (requestId === this.requestSequence) this.loading = false;
      }
    },
    findOwnComment() {
      const list = Array.isArray(this.comments) ? this.comments : [];
      const byCanUpdate = list.find(
        (item) => item && item.type !== "target" && item.can_update === true
      );
      if (byCanUpdate) return byCanUpdate;

      const authUserId = this.authUser?.id;
      if (authUserId == null) return null;
      return (
        list.find(
          (item) =>
            item &&
            item.type !== "target" &&
            item.user &&
            String(item.user.id) === String(authUserId)
        ) || null
      );
    },
    async submitAdminComment(text, entryType = "comment", visibility = "public") {
      if (this.submittingComment) return;

      const comment = String(text || "").trim();
      this.errorMessage = "";
      this.successMessage = "";

      if (entryType !== "comment") {
        this.errorMessage = this.$t("general.admin_comment_only");
        return;
      }
      if (!comment) {
        this.errorMessage = this.$t("general.comment_required");
        return;
      }

      // Normal yorum kişi başına bir tanedir; kendi yorumu varsa PUT ile güncellenir
      const isUpdate = !!this.findOwnComment();
      const body = {
        comment,
        visibility: visibility === "private" ? "private" : "public",
      };
      this.submittingComment = true;

      try {
        const url = `api/competencies-evaluation/${this.currentUserId}/${this.currentCompetencyId}/comment`;
        if (isUpdate) {
          await ApiService.put(url, body);
        } else {
          await ApiService.post(url, body);
        }

        await this.loadDetail(false);
        this.successMessage = isUpdate
          ? this.$t("general.admin_comment_updated")
          : this.$t("general.admin_comment_added");
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.errors?.comment?.[0] ||
          error?.message ||
          this.$t("general.comment_save_failed");
      } finally {
        this.submittingComment = false;
      }
    },
  },
};
</script>

<style scoped>
.development-competency-detail-page {
  margin: 0;
  max-width: 1180px;
  width: 100%;
}

.user-context-card {
  align-items: center;
  border: 1px solid #e5eaf0;
  border-radius: 12px;
  display: flex;
  gap: 14px;
  padding: 14px 18px;
}

.user-context-card .avatar {
  border: 2px solid #e3ebf5;
  flex: 0 0 50px;
}

.context-user-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.context-user-info span {
  color: #8b96a7;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
}

.context-user-info strong {
  color: #303b4b;
  font-size: 16px;
}

.context-user-info small {
  color: #718096;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-context-card .btn {
  margin-left: auto;
  white-space: nowrap;
}

.detail-view-wrapper {
  margin-top: 18px;
  position: relative;
}

.empty-state {
  color: #778295;
  padding: 64px 20px;
  text-align: center;
}

.empty-state i {
  color: #a9b6c7;
  font-size: 50px;
}

.empty-state h3 {
  color: #3d4756;
  margin: 12px 0 5px;
}

.empty-state .btn {
  margin-top: 10px;
}

@media (max-width: 640px) {
  .user-context-card {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .context-user-info {
    flex: 1;
  }

  .user-context-card .btn {
    margin-left: 0;
    width: 100%;
  }
}
</style>
